import { Suspense, lazy, useState } from "react";
import { ArrowRight } from "lucide-react";
import FadeIn, { SectionHeading } from "./FadeIn";
import TiltCard from "./motion/TiltCard";
import SolutionModal from "./SolutionModal";

const SolutionObject = lazy(() => import("./three/SolutionObject"));

const SOLUTIONS = [
  {
    title: "First-Time Buyers",
    obj: "starter",
    desc: "A clear, guided path into your first home — incentives, down-payment strategy and pre-approval handled with care.",
    img: "/homes/sol-starter.webp",
    span: "md:col-span-7",
    forWhom: "Canadians purchasing their very first home who want a clear, guided path from pre-approval to keys.",
    overview: "We map your full picture — income, savings, credit and goals — then build a step-by-step plan covering pre-approval, down-payment options and any incentive programs you may qualify for.",
    considerations: [
      "Get pre-approved before you shop so you know your true range",
      "Plan for closing costs beyond the down payment",
      "Understand how the mortgage stress test affects qualification",
      "Choose a rate structure that matches your timeline",
    ],
  },
  {
    title: "Move-Up Buyers",
    obj: "moveup",
    desc: "Seamlessly transition from your current home to the next, with timing and bridge strategies that protect you.",
    img: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=1200&auto=format&fit=crop",
    span: "md:col-span-5",
    forWhom: "Homeowners ready to sell their current property and move into a home that fits their next chapter.",
    overview: "We coordinate the sale and purchase so timing works in your favour — reviewing your equity position, portability options and bridge financing possibilities before you commit.",
    considerations: [
      "Unlock your current equity with a proper valuation strategy",
      "Align possession dates to avoid double-carrying costs",
      "Review whether porting your existing mortgage makes sense",
      "Get a fully reviewed pre-approval for the new purchase",
    ],
  },
  {
    title: "Refinancing",
    obj: "refinance",
    desc: "Restructure your mortgage to lower payments, consolidate debt or unlock equity for what matters next.",
    img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=1200&auto=format&fit=crop",
    span: "md:col-span-5",
    forWhom: "Homeowners who want their mortgage to work harder — lower costs, consolidated debt or access to equity.",
    overview: "A refinance restructures your mortgage around today's goals. We review your current terms, penalties and equity position before recommending whether a change genuinely benefits you.",
    considerations: [
      "Understand any prepayment penalties before making changes",
      "Clarify the goal — lower payment, access equity or consolidate",
      "An appraisal may be required to confirm property value",
      "Timing a refinance around renewal can reduce costs",
    ],
  },
  {
    title: "Investment Properties",
    obj: "investment",
    desc: "Build your portfolio with financing structured for cash flow, scalability and long-term wealth.",
    img: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1200&auto=format&fit=crop",
    span: "md:col-span-7",
    forWhom: "Buyers building long-term wealth through rental or investment real estate.",
    overview: "Investment financing is structured differently than a primary residence. We model cash flow, down-payment requirements and rental income treatment so the numbers hold up before you offer.",
    considerations: [
      "Down-payment requirements differ for non-owner-occupied properties",
      "Rental income is treated differently across lending policies",
      "Keep reserve funds for vacancies and maintenance",
      "Structure ownership and financing with your accountant",
    ],
  },
  {
    title: "Self-Employed",
    obj: "office",
    desc: "Income is more than a T4. We present your business story to lenders who understand entrepreneurs.",
    img: "https://images.unsplash.com/photo-1521791136064-7986c2920216?q=80&w=1200&auto=format&fit=crop",
    span: "md:col-span-7",
    forWhom: "Business owners, contractors and entrepreneurs whose income doesn't fit a standard T4.",
    overview: "Self-employed files reward thoughtful preparation. We present your business income story clearly and plan documentation well before you need the financing.",
    considerations: [
      "Two years of documented income is commonly reviewed",
      "Write-offs reduce taxable income — plan ahead with your accountant",
      "Keep business and personal finances clearly separated",
      "Start the conversation 6–12 months before you buy",
    ],
  },
  {
    title: "Private / Alternative Lending",
    obj: "private",
    desc: "Flexible capital solutions when traditional lenders can't move at the speed your situation demands.",
    img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=1200&auto=format&fit=crop",
    span: "md:col-span-5",
    forWhom: "Clients whose situation falls outside traditional lending criteria — credit events, unique income or tight timelines.",
    overview: "Private and alternative lending can bridge a gap when banks can't help. Every solution is built with full transparency and a clear exit strategy back to traditional financing.",
    considerations: [
      "Costs are typically higher than traditional financing",
      "These solutions are usually short-term by design",
      "A clear exit plan matters more than the rate",
      "Full transparency on fees and terms before you commit",
    ],
  },
  {
    title: "Debt Consolidation",
    obj: "debt",
    desc: "Roll high-interest balances into your home's equity — one lower payment and a clear path forward.",
    img: "/homes/scale-750.webp",
    span: "md:col-span-12",
    forWhom: "Homeowners carrying high-interest credit cards, lines of credit or loans who want one manageable monthly payment.",
    overview: "Your home's equity can be your lowest-cost source of capital. We assess your equity position and restructure high-interest debts into a single, planned mortgage solution — reducing total interest costs and simplifying your monthly obligations.",
    considerations: [
      "Consolidation should lower your total interest cost — we run the math first",
      "Prepayment penalties and fees are reviewed before restructuring anything",
      "A disciplined repayment plan keeps the debt from rebuilding",
      "Your home secures the new structure — we make sure you understand the trade-offs",
    ],
  },
];

export default function Solutions({ onLead }) {
  const [openIdx, setOpenIdx] = useState(null);
  const [hoverIdx, setHoverIdx] = useState(null);

  return (
    <section id="solutions" data-testid="solutions-section" className="py-24 lg:py-36 bg-charcoal-800/95 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <SectionHeading
            label="Mortgage Solutions"
            title="Solutions For Every Chapter"
            sub="From first keys to full portfolios — every strategy is designed around where you are, and where you're going."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {SOLUTIONS.map((s, i) => (
            <FadeIn key={s.title} delay={(i % 2) * 0.12} className={`col-span-1 ${s.span}`}>
              <TiltCard className="h-full">
                <article
                  data-testid={`solution-card-${i}`}
                  onClick={() => setOpenIdx(i)}
                  onMouseEnter={() => setHoverIdx(i)}
                  onMouseLeave={() => setHoverIdx(null)}
                  className="group relative h-80 md:h-96 overflow-hidden rounded-md border border-white/10 gold-border-hover cursor-pointer active:scale-[0.985] [transform-style:preserve-3d]"
                >
                  <div className="absolute inset-0" style={{ transform: "translateZ(-28px) scale(1.12)" }}>
                    <img
                      src={s.img}
                      alt={s.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/35 to-transparent" />
                  <div
                    className="absolute inset-0 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{ boxShadow: "inset 0 0 0 1px rgba(229,192,123,0.35)" }}
                  />
                  {hoverIdx === i && (
                    <div
                      className="absolute top-4 right-4 w-24 h-24 md:w-28 md:h-28 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none rounded-full bg-black/35 backdrop-blur-sm border border-gold/25 overflow-hidden"
                      style={{ transform: "translateZ(42px)" }}
                      aria-hidden="true"
                    >
                      <Suspense fallback={null}>
                        <SolutionObject type={s.obj} />
                      </Suspense>
                    </div>
                  )}
                  <div className="absolute inset-x-0 bottom-0 p-8" style={{ transform: "translateZ(34px)" }}>
                    <h3 className="font-serif text-2xl md:text-[26px] text-ivory mb-2.5">{s.title}</h3>
                    <p className="text-ivory/60 text-sm font-light leading-relaxed max-w-md mb-5 opacity-100 translate-y-0 md:opacity-0 md:translate-y-2 md:group-hover:opacity-100 md:group-hover:translate-y-0 transition-[opacity,transform] duration-500">
                      {s.desc}
                    </p>
                    <span
                      data-testid={`solution-learn-more-${i}`}
                      className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] text-gold group-hover:text-champagne transition-colors duration-300"
                    >
                      LEARN MORE <ArrowRight size={14} strokeWidth={1.5} />
                    </span>
                  </div>
                </article>
              </TiltCard>
            </FadeIn>
          ))}
        </div>
      </div>

      <SolutionModal
        solution={openIdx !== null ? SOLUTIONS[openIdx] : null}
        onClose={() => setOpenIdx(null)}
        onTalk={() => {
          setOpenIdx(null);
          onLead("consultation");
        }}
      />
    </section>
  );
}
